import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useId } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AspirationRole,
  useAddPersonAspiration,
  usePersonAspirations,
  useRemovePersonAspiration,
  useReorderPersonAspirations,
  useSetPersonAspirationLevel,
} from "@/hooks/use-person-aspirations";
import { usePersonRoles } from "@/hooks/use-persons";
import { useRoleLevelOptions } from "@/hooks/use-role-level-applicability";
import { cn } from "@/lib/utils";

/** Sentinel for "no level" — Radix Select forbids an empty-string item value. */
const NO_LEVEL = "__none__";

/**
 * The target-level picker for one aspiration, scoped to the levels the chosen
 * role opts into. Its own component so each row runs its own query for its own
 * role — the same shape as `RoleLevelSelect` in PersonRolesDialog.
 *
 * Renders nothing when the role uses no levels: an empty picker would read as
 * "you forgot something" rather than "this role has no ladder".
 *
 * Levels the server would refuse are shown but **disabled, with the reason in
 * the row** — already listed on another entry for this role, or already held.
 * Both were previously discoverable only by picking one and reading the error
 * toast; a level absent from the list would raise the same "why is it missing?"
 * question that the role picker did.
 */
function AspirationLevelSelect({
  roleId,
  value,
  onChange,
  disabled,
  label,
  hint,
  takenLevelIds,
  heldLevelId,
  className,
}: {
  roleId: string;
  value: string | null;
  onChange: (roleLevelId: string | null) => void;
  disabled?: boolean;
  /** Screen-reader name — the visible caption is the trigger's own value. */
  label: string;
  /**
   * Nudge rendered under the picker. It lives here rather than at the call site
   * so it can't appear for a role with no ladder — "pick the level" beside no
   * level picker is worse than saying nothing.
   */
  hint?: string;
  /**
   * Levels this person already has on ANOTHER entry for the same role — `null`
   * in the list means a level-less entry exists, which blocks "Any level".
   */
  takenLevelIds?: (string | null)[];
  /** The level they already hold in this role, which can't be worked toward. */
  heldLevelId?: string | null;
  className?: string;
}) {
  const { data: options = [], isLoading } = useRoleLevelOptions(roleId, roleId !== "");
  const labelId = useId();
  // Only levels this role opts into, never a retired one — matching what the
  // server's assertRoleLevelsApplicable will accept.
  const selectable = options.filter((o) => o.applies && !o.retired);

  if (roleId === "" || isLoading || selectable.length === 0) return null;

  const taken = new Set(takenLevelIds ?? []);
  /**
   * Why this level can't be picked, or null if it can. The entry's OWN current
   * value is never blocked — it's already selected, and disabling it would put
   * the trigger's own label out of reach.
   */
  function blockedReason(levelId: string | null): string | null {
    if (levelId === value) return null;
    if (taken.has(levelId)) return "already listed";
    if (levelId !== null && levelId === heldLevelId) return "already held";
    return null;
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <span id={labelId} className="sr-only">
        {label}
      </span>
      <Select
        value={value ?? NO_LEVEL}
        disabled={disabled}
        onValueChange={(v) => onChange(v === NO_LEVEL ? null : v)}
      >
        <SelectTrigger aria-labelledby={labelId} className="h-8 w-40 text-sm">
          <SelectValue placeholder="Any level" />
        </SelectTrigger>
        <SelectContent>
          {[{ id: null as string | null, name: "Any level" }, ...selectable].map((o) => {
            const reason = blockedReason(o.id);
            return (
              <SelectItem
                key={o.id ?? NO_LEVEL}
                value={o.id ?? NO_LEVEL}
                disabled={reason !== null}
              >
                {o.name}
                {reason && <span className="ml-1.5 text-muted-foreground text-xs">({reason})</span>}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

/**
 * Managing the roles a person is working toward — the editor half of
 * `PersonAspirationsSection`, which is read-only on the profile so the panel
 * stays a summary.
 *
 * Unlike Manage roles, edits apply as they're made rather than on a Save: the
 * API is per-action (add / retarget / reorder / remove), each with its own audit
 * row, and aspirations aren't effective-dated. So the footer says Done, not
 * Save — there is nothing left pending when it closes.
 *
 * Picking a role in the combobox flags it immediately; the level is then named on
 * its row. There is deliberately no Add button: a staged pick that needed a
 * second click was dropped whenever someone closed the dialog instead, and every
 * row can already change its level in place, so the step bought nothing.
 *
 * A role may be listed MORE THAN ONCE at different levels — Associate now, Staff
 * later — so every row is keyed and addressed by the entry's own id, and the
 * picker offers every active role rather than hiding the listed ones. What it
 * may NOT be is the same role at the same level twice, or a rung the person
 * already holds; both are disabled in the level picker with the reason shown,
 * and refused server-side as the backstop.
 */
export function PersonAspirationsDialog({
  open,
  onOpenChange,
  personId,
  personName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
  personName: string;
}) {
  const { data, isLoading } = usePersonAspirations(personId);
  /**
   * The person's own role assignments — the source of "already held", read here
   * rather than passed in because the LEVEL matters, not just the role id. Dates
   * are deliberately ignored, matching the server guard: a level held on a role
   * with an end date still can't be worked toward.
   */
  const { data: heldRoles = [] } = usePersonRoles(open ? personId : null);
  const add = useAddPersonAspiration(personId);
  const remove = useRemovePersonAspiration(personId);
  const reorder = useReorderPersonAspirations(personId);
  const setLevel = useSetPersonAspirationLevel(personId);

  const aspirations: AspirationRole[] = data?.aspirations ?? [];
  const busy = add.isPending || remove.isPending || reorder.isPending || setLevel.isPending;

  /** The level this person holds in a role, if any. */
  function heldLevelFor(roleId: string): string | null {
    return heldRoles.find((r) => r.roleId === roleId)?.roleLevelId ?? null;
  }
  function isHeldRole(roleId: string): boolean {
    return heldRoles.some((r) => r.roleId === roleId);
  }

  function move(index: number, delta: -1 | 1) {
    const ids = aspirations.map((a) => a.id);
    const target = index + delta;
    const [a, b] = [ids[index], ids[target]];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    reorder.mutate(ids);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Working toward</DialogTitle>
          <DialogDescription>
            The roles {personName} wants to work toward, in priority order — first choice at the
            top. Name the career level being aimed at where the role has one. Changes save as you
            make them.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {["a", "b", "c"].map((k) => (
              <Skeleton key={k} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {aspirations.length === 0 ? (
              <p className="text-muted-foreground text-sm">Nothing flagged yet.</p>
            ) : (
              <ul className="space-y-1">
                {aspirations.map((a, i) => {
                  const entryName = a.roleLevelName
                    ? `${a.roleName} at ${a.roleLevelName}`
                    : a.roleName;
                  return (
                    <li key={a.id} className="space-y-2 rounded-md border bg-card px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          title={`Priority ${a.rank}`}
                          className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-xs"
                        >
                          {a.rank}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm">{a.roleName}</span>
                        {isHeldRole(a.roleId) && (
                          <span
                            title="Already in this role, at a different level"
                            className="shrink-0 rounded-sm bg-muted px-1.5 py-0.5 font-medium text-3xs text-muted-foreground uppercase tracking-wide"
                          >
                            Current
                          </span>
                        )}
                        {aspirations.length > 1 && (
                          <div className="flex shrink-0 flex-col">
                            <button
                              type="button"
                              aria-label={`Move ${entryName} up`}
                              title="Move up"
                              disabled={busy || i === 0}
                              onClick={() => move(i, -1)}
                              className="flex h-4 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-30"
                            >
                              <ChevronUp className="size-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Move ${entryName} down`}
                              title="Move down"
                              disabled={busy || i === aspirations.length - 1}
                              onClick={() => move(i, 1)}
                              className="flex h-4 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-30"
                            >
                              <ChevronDown className="size-3.5" />
                            </button>
                          </div>
                        )}
                        {/* Guarded like every other removal: this deletes the
                            entry and re-ranks the list immediately, with no
                            undo, from a small X. */}
                        <ConfirmDialog
                          title="Remove this aspiration?"
                          description={`${entryName} is removed from what ${personName} is working toward, and the remaining entries are re-ranked. This can't be undone.`}
                          confirmLabel="Remove"
                          destructive
                          onConfirm={() => remove.mutate(a.id)}
                          trigger={
                            <button
                              type="button"
                              title={`Remove ${entryName}`}
                              aria-label={`Remove ${entryName}`}
                              disabled={busy}
                              className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40"
                            >
                              <X className="size-4" />
                            </button>
                          }
                        />
                      </div>
                      {/* Retargeting is a one-control change, so it lives on the
                        row rather than behind a remove-and-re-add. */}
                      <AspirationLevelSelect
                        roleId={a.roleId}
                        value={a.roleLevelId}
                        disabled={busy}
                        label={`Level ${personName} is working toward in ${a.roleName}`}
                        /* A role already held, aimed at no particular rung, says
                         nothing — so prompt for the level instead of refusing
                         the row (what forced a two-step add before). */
                        hint={
                          isHeldRole(a.roleId) && a.roleLevelId === null
                            ? "Already in this role — pick the level being worked toward."
                            : undefined
                        }
                        /* Rungs of THIS role already spoken for: the other
                           entries' levels, plus the one they hold. */
                        takenLevelIds={aspirations
                          .filter((o) => o.roleId === a.roleId && o.id !== a.id)
                          .map((o) => o.roleLevelId)}
                        heldLevelId={heldLevelFor(a.roleId)}
                        onChange={(roleLevelId) =>
                          setLevel.mutate({ aspirationId: a.id, roleLevelId })
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="border-t pt-3">
              <EntityCombobox
                entity="roles"
                value={null}
                filters={{ status: "active" }}
                /* Nothing is excluded: a listed role can be listed again at a
                   different level, and a held role at a different rung is the
                   whole point. The server refuses only an exact duplicate pair
                   (or the rung already held), with a message saying which. */
                placeholder="Add a role…"
                searchPlaceholder="Search roles…"
                disabled={busy}
                /* value stays null so the trigger keeps reading "Add a role…"
                   rather than the last thing added — the row below IS the
                   feedback that it landed. */
                onChange={(roleId) => {
                  if (roleId) add.mutate({ roleId, roleLevelId: null });
                }}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" disabled={busy} onClick={() => onOpenChange(false)}>
            {busy ? "Saving…" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
